import ManualAUthorizationModal from 'components/modals/ManualAuthorizationModal';
import Service from 'models/ServiceModel';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'helpers/axios';
import config from 'helpers/config';
import moment from 'moment';
import dateFormats from 'helpers/dateFormats';
import Authorization from 'models/AuthorizationModel';
import AuthorizationType from 'models/types/AuthorizationType';
import AuthorizationModal from 'components/pages/estimate/AuthorizationModal';
import ServiceAuthorizationStatus from 'models/types/ServiceAuthorizationStatus';
import Price from 'components/common/Price';
import { IoMdCheckmark, IoMdClose } from 'react-icons/io';
import HoverTooltip from 'components/common/HoverTooltip';

interface Props {
    services: Service[];
    repairOrderId: string;
}

interface AuthorizationWithTotals extends Authorization {
    totalCents: number;
    deltaCents: number;
}

const getMethodName = (type: AuthorizationType) => {
    switch (type) {
        case AuthorizationType.Customer:
            return 'Signature';
        case AuthorizationType.ManualInPerson:
            return 'Verbal In-Person';
        case AuthorizationType.ManualByPhone:
            return 'Verbal By Phone';
        case AuthorizationType.ManualByText:
            return 'By SMS';
        case AuthorizationType.ManualyByEmail:
            return 'By Email';
        default:
            return 'Unknown';
    }
};

const RepairOrderAuthorizations: React.FC<Props> = ({ services, repairOrderId }: Props) => {
    const [isManualAuthModalOpen, setIsManualAuthModalOpen] = useState(false);
    const [authorizations, setAuthorizations] = useState<AuthorizationWithTotals[]>([]);
    const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
    const [authorizationToView, setAuthorizationToView] = useState<Authorization | undefined>();

    const fetchAuthorizations = useCallback(() => {
        axios
            .get(`${config.apiUrl}/authorization/${repairOrderId}`)
            .then((res) => setAuthorizations(res.data))
            .catch((err) => {
                console.log(err);
            });
    }, [repairOrderId]);

    useEffect(() => {
        fetchAuthorizations();
    }, [fetchAuthorizations]);

    const getAuthorizationStatusName = (status: ServiceAuthorizationStatus) => {
        switch (status) {
            case ServiceAuthorizationStatus.NotAuthorized: {
                return 'Not Authorized';
            }
            case ServiceAuthorizationStatus.Authorized: {
                return 'Authorized';
            }
            case ServiceAuthorizationStatus.Declined: {
                return 'Declined';
            }
        }
    };

    const { authorizedTotal, pendingAuthorization, declinedTotal } = useMemo(() => {
        let authorizedTotal = 0;
        let pendingAuthorization = 0;
        let declinedTotal = 0;
        services?.forEach((service) => {
            // Tax-inclusive grand total — the amount the customer actually authorizes / is billed.
            const serviceTotal = service.grandTotalCents || 0;
            if (service.authorizationStatus === ServiceAuthorizationStatus.Authorized) {
                authorizedTotal += serviceTotal;
            } else if (service.authorizationStatus === ServiceAuthorizationStatus.Declined) {
                declinedTotal += serviceTotal;
            } else if (service.authorizationStatus === ServiceAuthorizationStatus.NotAuthorized) {
                pendingAuthorization += serviceTotal;
            }
        });

        return { authorizedTotal, pendingAuthorization, declinedTotal };
    }, [services]);

    const latestAuthorization = authorizations[0];

    return (
        <div className="overflow-hidden">
            <div>
                {services?.length ? (
                    <>
                        <div className="space-y-2 mb-4">
                            {services.map((service, index) => (
                                <div key={index} className="flex items-center">
                                    <div className="mr-2">
                                        <HoverTooltip content={getAuthorizationStatusName(service.authorizationStatus)}>
                                            {service.authorizationStatus === ServiceAuthorizationStatus.Authorized ? (
                                                <div className="h-5 w-5 rounded border border-green-500 bg-green-50 flex items-center justify-center">
                                                    <IoMdCheckmark className="text-green-600" />
                                                </div>
                                            ) : service.authorizationStatus === ServiceAuthorizationStatus.Declined ? (
                                                <div className="h-5 w-5 rounded border border-red-500 bg-red-50 flex items-center justify-center">
                                                    <IoMdClose className="text-red-600" />
                                                </div>
                                            ) : (
                                                <div className="h-5 w-5 rounded border border-gray-300"></div>
                                            )}
                                        </HoverTooltip>
                                    </div>

                                    <div className="flex-1">{service.title}</div>
                                    <div className="font-semibold">
                                        <Price price={service.grandTotalCents || 0} isInCents={true} />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-gray-200 pt-3 space-y-1">
                            <div className="flex justify-between">
                                <span className="font-semibold">Authorized Total:</span>
                                <span className="text-green-600 font-semibold">
                                    <Price price={authorizedTotal} isInCents={true} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-semibold">Pending Authorization:</span>
                                <span className="text-amber-600 font-semibold">
                                    <Price price={pendingAuthorization} isInCents={true} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-semibold">Declined Total:</span>
                                <span className="text-red-600 font-semibold">
                                    <Price price={declinedTotal} isInCents={true} />
                                </span>
                            </div>
                        </div>

                        {!!latestAuthorization && (
                            <div className="mt-4 pt-3 border-t border-gray-200 text-sm italic text-gray-600">
                                Grand Total <Price price={latestAuthorization.totalCents} isInCents={true} /> authorized by{' '}
                                <span className="font-semibold not-italic">{latestAuthorization.authorizedByName}</span> on{' '}
                                {moment(latestAuthorization.createdAt).format(dateFormats.USA_DATETIME)}{' '}
                                {getMethodName(latestAuthorization.type).toLowerCase()}
                            </div>
                        )}

                        {authorizations.length > 0 && (
                            <div className="mt-4 border-t border-gray-200 ">
                                <h3 className="text-lg font-semibold mb-2">Authorization history</h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-gray-500 border-b border-gray-200">
                                                <th className="py-1 pr-2 font-semibold">Method</th>
                                                <th className="py-1 pr-2 font-semibold">Authorizer</th>
                                                <th className="py-1 pr-2 font-semibold text-right">RO Total</th>
                                                <th className="py-1 pr-2 font-semibold">Date</th>
                                                <th className="py-1 font-semibold">IP</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {authorizations.map((auth) => (
                                                <tr key={auth.id} className="border-b border-gray-100">
                                                    <td className="py-2 pr-2">
                                                        {getMethodName(auth.type)}
                                                        {!!auth.authorizationSignature && (
                                                            <span
                                                                className="ml-2 clickable text-blue-500 font-semibold text-xs hover:underline hover:text-blue-600"
                                                                onClick={() => {
                                                                    setIsSignatureModalOpen(true);
                                                                    setAuthorizationToView(auth);
                                                                }}
                                                            >
                                                                View
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-2 pr-2">{auth.authorizedByName}</td>
                                                    <td className="py-2 pr-2 text-right">
                                                        <div className="font-semibold">
                                                            <Price price={auth.totalCents} isInCents={true} />
                                                        </div>
                                                        {auth.deltaCents !== 0 && (
                                                            <div className="text-xs text-gray-400">
                                                                {auth.deltaCents > 0 ? '+' : ''}
                                                                <Price price={auth.deltaCents} isInCents={true} />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="py-2 pr-2 whitespace-nowrap">
                                                        {moment(auth.createdAt).format(dateFormats.USA_DATETIME)}
                                                    </td>
                                                    <td className="py-2 text-gray-500">{auth.ipAddress || '--'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="mt-3">
                            <button
                                className="bg-blue-600 text-white w-full rounded py-2 font-semibold hover:bg-blue-700"
                                onClick={() => setIsManualAuthModalOpen(!isManualAuthModalOpen)}
                            >
                                Manage Authorization
                            </button>
                        </div>

                        {isSignatureModalOpen && (
                            <AuthorizationModal
                                authorization={authorizationToView!}
                                onClose={() => {
                                    setIsSignatureModalOpen(false);
                                    setAuthorizationToView(undefined);
                                }}
                                services={services}
                            />
                        )}

                        {!!isManualAuthModalOpen && (
                            <ManualAUthorizationModal
                                repairOrderId={repairOrderId}
                                services={services}
                                onClose={(authorizationCreated) => {
                                    setIsManualAuthModalOpen(false);
                                    if (authorizationCreated) {
                                        fetchAuthorizations();
                                    }
                                }}
                            />
                        )}
                    </>
                ) : (
                    <div className="font-bold text-sm text-gray-700 text-center">No services to authorize yet</div>
                )}
            </div>
        </div>
    );
};

export default RepairOrderAuthorizations;
